// =====================================================================================================================
// --- Scan timings ---
// =====================================================================================================================

// The overall time that the user has to complete the scan.
export const SCAN_TIMEOUT_IN_MILLISECONDS = 20000; // 20 seconds

// How often we execute the face-mesh machine learning model.
export const SCAN_INTERVAL_IN_MILLISECONDS = 40; // 25 times a second

// What is the minimum scan time that should be displayed to the user?
//
// This is a perception filter for the user. If we capture a scan too quickly it might appear to the user that nothing
// was done.  So if it only took 1 second to do a scan, we want the user to wait extra time to add the perception
// that scan is more complex and complicated than it is.  A real world analogy to this is that Beats headphones have
// weights embedded in them.  The weights do not affect the quality of the sound but a consumer will value the weight
// as something of higher quality and pay a premium for it.
//
// This value should always be less than SCAN_TIMEOUT_IN_MILLISECONDS.
export const MINIMUM_SCAN_TIME_IN_MILLISECONDS = 7500; // 7.5 seconds

// =====================================================================================================================
// --- Glasses detection ---
// =====================================================================================================================

// How many times in a row we need to detect glasses before setting that glasses are detected.
export const GLASSES_ON_DIFFERENCE_THRESHOLD = 3;

// How many times in a row we need to detect no glasses before going out of no glass mode.
export const GLASSES_OFF_DIFFERENCE_THRESHOLD = 3;

// Should we check for glasses?  Can conveniently be turned off temporarily for developers that wear glasses during testing.
export const GLASSES_CHECK_ENABLED = true;

// =====================================================================================================================
// --- Mouth open detection ---
// =====================================================================================================================

// Should we check for mouth open?
//
// This should be turned off because our current algorithm does not work with all people
// and can cause false positives that make using the scanner frustrating to use.  It can be re-enabled when we get
// a better algorithm.
export const MOUTH_OPEN_CHECK_ENABLED = false

// =====================================================================================================================
// --- Blur detection ---
// =====================================================================================================================

export const BLUR_DETECTION_ENABLED = true

// =====================================================================================================================
// --- Scan Angle constraints ---
// =====================================================================================================================

// Should we constrain the head position on the X axis
export const CONSTRAIN_ON_X_AXIS = false;

// Head is tilted no more than X degrees up or down on x-axis.
export const X_ANGLE_DISTANCE_VARIANCE = 8;

// Head is tilted no more than Y degrees up or down on y-axis.
export const Y_ANGLE_DISTANCE_VARIANCE = 4;

// What is the ideal X angle for all images?  The angle accepted will be within the range of this angle
// plus or minus X_ANGLE_DISTANCE_VARIANCE
export const IDEAL_X_ANGLE = 180;

// What is the ideal angle from the front?  The angle accepted will be within the range of this angle
// plus or minus Y_ANGLE_DISTANCE_VARIANCE
export const IDEAL_Y_FRONT_ANGLE = 0;

// What is the ideal angle turned to the left?  The angle accepted will be within the range of this angle
// plus or minus Y_ANGLE_DISTANCE_VARIANCE
export const IDEAL_Y_LEFT_ANGLE = 20;

// What is the ideal angle turned to the right?  The angle accepted will be within the range of this angle
// plus or minus Y_ANGLE_DISTANCE_VARIANCE
export const IDEAL_Y_RIGHT_ANGLE = -20;

// =====================================================================================================================
// --- Analysis settings ---
// =====================================================================================================================

// Time in milliseconds to throttle analysis between steps.  Sending lots of data to TensorFlow during the analysis phase
// is very resource intensive.  The analysis has a configurable throttle interval which sleeps in between steps. This
// gives the device breathing room to do garbage collection, render UI, etc.
//
// Currently, each image is sent to the machine 16 times (6 burn frames and 10 analysis frames) and there are 3 images.
// So setting this value to 50ms will add (16 frames x 3 frames x 50 ms) = 2400 ms or 2.4 seconds to the analyze page.
//
export const THROTTLE_INTERVAL_IN_MILLISECONDS = 50

// =====================================================================================================================
// --- Post-analysis settings ---
// =====================================================================================================================

// What is the minimum face height in millimeters that we will allow?
export const MIN_FACE_HEIGHT_MM = 20;

// What is the maximum face height in millimeters that we will allow?
export const MAX_FACE_HEIGHT_MM = 150;

// What is the minimum nose width in millimeters that we will allow?
export const MIN_NOSE_WIDTH_MM = 5;

// What is the maximum nose width in millimeters that we will allow?
export const MAX_NOSE_WIDTH_MM = 75;

// What is the minimum nose depth in millimeters that we will allow?
export const MIN_NOSE_DEPTH_MM = 2;

// What is the maximum nose depth in millimeters that we will allow?
export const MAX_NOSE_DEPTH_MM = 50;

// =====================================================================================================================
// --- User Interface ---
// =====================================================================================================================

export const SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS = 400;

// Should the double crosshair be shown?  Introduced in version 2.10 as fallback so that we can revert easily if need be.
export const SHOW_DOUBLE_CROSSHAIR = true;
